import { useState } from 'react';
import type { BookGenre } from '@booknest/shared';

import { GENRE_META, genreMeta } from '@/features/discover/genre-meta.js';
import {
  useMyFollows,
  useToggleFollowGenre,
} from '@/features/follows/hooks/useMyFollows.js';

/**
 * Carte "Genres suivis" — pendant de `AuthorsList` pour les genres. L'user
 * choisit ses genres dans un picker, les chips actives ont la cloche, click
 * pour unfollow. Le picker est replié par défaut.
 */
export function FollowedGenresCard(): JSX.Element {
  const { data: follows } = useMyFollows();
  const toggleMutation = useToggleFollowGenre();
  const [pickerOpen, setPickerOpen] = useState(false);

  const followed = follows?.genres ?? [];
  const followedSet = new Set(followed);

  const handleToggle = (genre: string, alreadyFollowed: boolean): void => {
    toggleMutation.mutate({ genre, followed: alreadyFollowed });
  };

  return (
    <div className="authors-card">
      <div className="flex items-center justify-between">
        <h3 className="!mb-0">🎨 Genres suivis</h3>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="rounded-full bg-cream-light px-2.5 py-1 text-[11px] font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
        >
          {pickerOpen ? 'Fermer' : '＋ Ajouter'}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {followed.length === 0 && !pickerOpen && (
          <p className="text-sm text-brown-soft">
            Aucun genre suivi. Clique sur « ＋ Ajouter » pour choisir.
          </p>
        )}
        {followed.map((g) => {
          const meta = genreMeta(g as BookGenre);
          return (
            <button
              key={g}
              type="button"
              onClick={() => handleToggle(g, true)}
              disabled={toggleMutation.isPending}
              aria-label={`Ne plus suivre ${meta.label}`}
              className="flex items-center gap-1.5 rounded-full bg-terracotta px-3 py-1.5 text-[11px] font-semibold text-cream-light hover:bg-terracotta-dark disabled:opacity-50"
            >
              <span aria-hidden>{meta.emoji}</span>
              {meta.label}
              <span aria-hidden className="ml-0.5 opacity-75">
                ✕
              </span>
            </button>
          );
        })}
      </div>

      {pickerOpen && (
        <div className="mt-4 flex flex-wrap gap-1.5 border-t border-cream-dark pt-3">
          {GENRE_META.filter((m) => m.value !== 'other').map((m) => {
            const isFollowed = followedSet.has(m.value);
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => handleToggle(m.value, isFollowed)}
                disabled={toggleMutation.isPending}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                  isFollowed
                    ? 'bg-cream-dark text-brown ring-1 ring-brown-light'
                    : 'bg-cream-light text-brown ring-1 ring-cream-dark hover:bg-cream-dark'
                }`}
              >
                <span aria-hidden>{m.emoji}</span>
                {m.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
